import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { QrCodeGenerator } from '@/components/QrCodeGenerator';
import { useQrCode } from '@/hooks/useQrCode';
import i18n from '@/translations/config';
import { Card } from '@/types/card';
import { useRef } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { FaCopy, FaDownload } from 'react-icons/fa6';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  card: Card;
}

export const ShareModal = ({ isOpen, onClose, card }: ShareModalProps) => {
  const { t } = useTranslation();
  const svgRef = useRef<SVGSVGElement>(null);
  const { shareQrCode } = useQrCode();

  const cardUrl = `${window.location.origin}/${i18n.language}/profile/${card.slug}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(cardUrl);
      toast.success(t('cardsList:share.linkCopied'));
    } catch (error) {
      toast.error(t('cardsList:share.errorCopyingLink'));
    }
  };

  const handleShare = () => {
    if (svgRef.current) {
      shareQrCode(svgRef.current, card);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('cardsList:share.title')}
      className="max-w-md"
    >
      <div className="space-y-6 p-4">
        {/* QR Code section */}
        <div className="flex flex-col items-center justify-center rounded-lg bg-gray-50 p-6">
          <QrCodeGenerator
            slug={card.slug}
            qrColor={card.qr_code_color}
            className="max-w-[200px]"
            svgRef={svgRef}
          />
        </div>

        {/* Share buttons section */}
        <div className="flex justify-center">
          <Button onClick={handleShare} className="gap-2">
            <FaDownload /> {t('common:download')}
          </Button>
        </div>

        {/* Link section */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            {t('cardsList:share.cardLink')}
          </label>
          <div className="relative flex items-center">
            <input
              type="text"
              readOnly
              value={cardUrl}
              className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 pr-10 text-sm focus:outline-none"
            />
            <button
              onClick={handleCopyLink}
              className="absolute right-2 rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              title={t('cardsList:share.copyLink')}
            >
              <FaCopy className="size-4" />
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
